import { useRef, useState, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { c as __awaiter, d as __generator, b as __spreadArray } from './common.js';
import { parse } from 'qs';
import { debounce } from 'lodash';
import { openPopup } from '@bucketplace/lib';
import { g as getDeviceInfo, i as isHigherAppVersion } from './utils.js';
import { B as BREAK_POINT } from './foundation.js';

var HANGUL_INITIAL = 'rRseEfaqQtTdwWczxvg'.split('');
var HANGUL_MEDIAL = 'k,o,i,O,j,p,u,P,h,hk,ho,hl,y,n,nj,np,nl,b,m,ml,l'.split(',');
var HANGUL_FINAL = (',r,R,rt,s,sw,sg,e,f,fr,fa,fq,ft,fx,fv,fg,a,q,qt,t,T,' + 'd,w,c,z,x,v,g').split(',');
var HANGUL_JAMO = (',r,R,rt,s,sw,sg,e,E,f,fr,fa,fq,ft,fx,fv,fg,a,q,Q,qt,t,' + 'T,d,w,W,c,z,x,v,g,k,o,i,O,j,p,u,P,h,hk,ho,hl,y,n,nj,np,nl,b,m,ml,l').split(',');
function decomposeHangul(input) {
  return input.split('').map(function (char) {
    var code = char.charCodeAt(0);
    if (code >= 0xAC00 && code <= 0xD7A3) {
      // Decompose hangul to dubeolsik characters.
      code -= 0xAC00;
      var final = HANGUL_FINAL[code % HANGUL_FINAL.length];
      code = code / HANGUL_FINAL.length | 0;
      var medial = HANGUL_MEDIAL[code % HANGUL_MEDIAL.length];
      code = code / HANGUL_MEDIAL.length | 0;
      var initial = HANGUL_INITIAL[code % HANGUL_INITIAL.length];
      return [initial, medial, final].join('');
    } else if (code >= 0x3130 && code <= 0x3163) {
      return HANGUL_JAMO[code - 0x3130];
    }
    return char;
  }).join('');
}

/**
 * Determines if the given value has changed.
 * @param {any} value The value to compare against.
 * @param {function} isEqual The checking mechanism. Defaults to shallow equal.
 * @return {boolean} Whether if the value has changed.
 */
function useChanged(value, isEqual) {
  if (isEqual === void 0) {
    isEqual = function isEqual(a, b) {
      return a === b;
    };
  }
  var prevValueRef = useRef();
  if (prevValueRef.current == null) {
    prevValueRef.current = {
      value: value
    };
    return false;
  }
  var prevValue = prevValueRef.current.value;
  var changed = !isEqual(prevValue, value);
  prevValueRef.current.value = value;
  return changed;
}

function useFocused() {
  var _a = useState(false),
    isFocused = _a[0],
    setFocused = _a[1];
  var wasClicked = useRef(false);
  var handleFocus = useCallback(function () {
    setFocused(true);
  }, []);
  var handleClick = useCallback(function () {
    wasClicked.current = true;
  }, []);
  var handleKeyDown = useCallback(function (e) {
    if (e.keyCode === 9) {
      // Tab
      setFocused(false);
    }
  }, []);
  useEffect(function () {
    if (isFocused) {
      var handleClickCapture_1 = function handleClickCapture_1() {
        wasClicked.current = false;
      };
      var handleClick_1 = function handleClick_1() {
        if (!wasClicked.current) {
          setFocused(false);
        }
      };
      window.addEventListener('click', handleClick_1);
      window.addEventListener('click', handleClickCapture_1, true);
      return function () {
        window.removeEventListener('click', handleClick_1);
        window.removeEventListener('click', handleClickCapture_1, true);
      };
    }
  }, [isFocused]);
  return [isFocused, setFocused, {
    handleFocus: handleFocus,
    handleClick: handleClick,
    handleKeyDown: handleKeyDown
  }];
}

function canInferQuery(query, oldQuery) {
  if (query == null || oldQuery == null) {
    return false;
  }
  var queryDecompsed = decomposeHangul(query);
  var oldQueryDecomposed = decomposeHangul(oldQuery);
  return queryDecompsed.startsWith(oldQueryDecomposed);
}
// Creates simple query cache for useAutoCompleteFetch.
function createCachedFetch(onFetch, shouldCache) {
  var cache = {};
  return function (query) {
    var queryDecomposed = query == null ? '' : decomposeHangul(query);
    if (cache[queryDecomposed] != null) {
      return Promise.resolve(cache[queryDecomposed]);
    }
    return onFetch(query).then(function (value) {
      if (shouldCache != null && !shouldCache(query)) {
        return value;
      }
      cache[queryDecomposed] = value;
      return value;
    });
  };
}
function useAutoCompleteFetch(pageKey, query, isEnabled, onFetch, isValueEmpty) {
  var hasDisabledRef = useRef(false);
  var pendingRef = useRef(false);
  var _a = useState(function () {
      return {
        pageKey: null,
        query: null,
        value: null
      };
    }),
    data = _a[0],
    setData = _a[1];
  var handleSetData = useCallback(function (data) {
    return setData(function (prev) {
      return {
        pageKey: prev.pageKey,
        query: prev.query,
        value: typeof data === 'function' ? data(prev.value) : data
      };
    });
  }, []);
  var shouldLoad = isEnabled && (data == null || pageKey !== data.pageKey || query !== data.query || hasDisabledRef.current);
  // If the query completely extends previous query and previous query was
  // empty, the query would be empty too - don't fetch if that's the case.
  if (shouldLoad && !hasDisabledRef.current && data != null && data.value != null && pageKey === data.pageKey && canInferQuery(query, data.query) && isValueEmpty != null) {
    shouldLoad = !isValueEmpty(data.value);
  }
  useEffect(function () {
    if (shouldLoad) {
      if (pendingRef.current) {
        return;
      }
      (function () {
        return __awaiter(this, void 0, void 0, function () {
          var result, e_1;
          return __generator(this, function (_a) {
            switch (_a.label) {
              case 0:
                pendingRef.current = true;
                _a.label = 1;
              case 1:
                _a.trys.push([1, 3,, 4]);
                return [4 /*yield*/, onFetch(query)];
              case 2:
                result = _a.sent();
                setData({
                  pageKey: pageKey,
                  query: query,
                  value: result
                });
                hasDisabledRef.current = false;
                pendingRef.current = false;
                return [3 /*break*/, 4];
              case 3:
                e_1 = _a.sent();
                hasDisabledRef.current = false;
                pendingRef.current = false;
                throw e_1;
              case 4:
                return [2 /*return*/];
            }
          });
        });
      })();
    }
  }, [pageKey, query, data, isEnabled, onFetch, shouldLoad]);
  if (!isEnabled) {
    hasDisabledRef.current = true;
    return [null, handleSetData];
  }
  if (data == null || pageKey !== data.pageKey) {
    return [null, handleSetData];
  }
  // Otherwise, return the current value.
  return [data.value, handleSetData];
}

function useAutoComplete(inputRef, options) {
  var pageKey = options.pageKey,
    isAlwaysFocused = options.isAlwaysFocused,
    onFetch = options.onFetch,
    getItemQuery = options.getItemQuery,
    inputQuery = options.query,
    _a = options.initialQuery,
    initialQuery = _a === void 0 ? null : _a,
    onFocus = options.onFocus,
    onSubmit = options.onSubmit,
    onSubmitItem = options.onSubmitItem;
  var _b = useState(initialQuery),
    currentQuery = _b[0],
    setQuery = _b[1];
  var query = currentQuery == null ? inputQuery : currentQuery;
  var _c = useFocused(),
    isCurrentlyFocused = _c[0],
    setFocused = _c[1],
    focusCallbacks = _c[2];
  var _d = useState(false),
    isOpened = _d[0],
    setOpened = _d[1];
  var ignoreMouseUntilRef = useRef(0);
  var isFocused = isAlwaysFocused || isCurrentlyFocused;
  var _e = useState({
      selected: -1,
      hovered: -1
    }),
    currentIndex = _e[0],
    setIndex = _e[1];
  var setSelectedIndex = useCallback(function (index) {
    setIndex(function (prev) {
      var nextIndex = typeof index === 'function' ? index(prev.hovered) : index;
      return {
        selected: nextIndex,
        hovered: nextIndex
      };
    });
  }, []);
  var setHoveredIndex = useCallback(function (index) {
    if (Date.now() > ignoreMouseUntilRef.current) {
      setIndex(function (prev) {
        return {
          selected: prev.selected,
          hovered: index
        };
      });
    }
  }, []);
  var handleSubmit = useCallback(function (value) {
    if (value == null || value.trim() === '') {
      // Do nothing if value is empty
      return;
    }
    if (inputRef.current != null) {
      inputRef.current.blur();
    }
    setFocused(false);
    setOpened(false);
    onSubmit(value);
  }, [onSubmit, inputRef, setFocused]);
  var handleSubmitItem = useCallback(function (item, index) {
    if (inputRef.current != null) {
      inputRef.current.blur();
    }
    setFocused(false);
    setOpened(false);
    onSubmitItem(item, index);
  }, [onSubmitItem, inputRef, setFocused]);
  var handleReset = useCallback(function () {
    setQuery(null);
  }, []);
  var data = useAutoCompleteFetch(pageKey, query, isFocused, onFetch)[0];
  var maxIndex = data == null ? 0 : data.length;
  var shownQuery = currentIndex.selected >= 0 && isOpened && data != null && data[currentIndex.selected] != null ? getItemQuery(data[currentIndex.selected]) : query;
  var handleInputChange = useCallback(function (value) {
    setFocused(true);
    setQuery(value);
    setSelectedIndex(-1);
  }, [setQuery, setSelectedIndex, setFocused]);
  if (isOpened && !isFocused) {
    setOpened(false);
  }
  var hasDataChanged = useChanged(data);
  if (hasDataChanged && maxIndex > 0) {
    setTimeout(function () {
      ignoreMouseUntilRef.current = Date.now() + 100;
      setSelectedIndex(-1);
      setOpened(true);
    });
  }
  var hasPageChanged = useChanged(pageKey);
  if (hasPageChanged) {
    setQuery(null);
  }
  var handleInputKeyDown = useCallback(function (e) {
    focusCallbacks.handleKeyDown(e);
    switch (e.keyCode) {
      case 27:
        // ESC
        setOpened(false);
        break;
      case 13:
        // Return
        if (data != null && currentIndex.hovered >= 0) {
          handleSubmitItem(data[currentIndex.hovered], currentIndex.hovered);
        } else {
          handleSubmit(shownQuery);
        }
        e.preventDefault();
        break;
      case 9:
        // Tab
        // Commit current shownQuery
        if (query !== shownQuery) {
          setQuery(shownQuery);
        }
        break;
      case 38:
        // Up arrow
        e.preventDefault();
        ignoreMouseUntilRef.current = Date.now() + 100;
        if (isOpened) {
          setSelectedIndex(function (prev) {
            return prev <= -1 ? maxIndex - 1 : prev - 1;
          });
        } else if (maxIndex > 0) {
          setOpened(true);
          setSelectedIndex(maxIndex - 1);
        }
        break;
      case 40:
        // Down arrow
        e.preventDefault();
        ignoreMouseUntilRef.current = Date.now() + 100;
        if (isOpened) {
          setSelectedIndex(function (prev) {
            return prev >= maxIndex - 1 ? 0 : prev + 1;
          });
        } else if (maxIndex > 0) {
          setOpened(true);
          setSelectedIndex(0);
        }
        break;
      case 37:
        // Left arrow
        setQuery(shownQuery);
        setSelectedIndex(-1);
        break;
      case 39:
        // Right arrow
        setQuery(shownQuery);
        setSelectedIndex(-1);
        break;
    }
  }, [maxIndex, data, isOpened, query, shownQuery, currentIndex.hovered, handleSubmit, handleSubmitItem, focusCallbacks, setSelectedIndex]);
  var handleListMouseEnter = useCallback(function (_, i) {
    return setHoveredIndex(i);
  }, [setHoveredIndex]);
  var handleInputFocus = useCallback(function (e) {
    focusCallbacks.handleFocus(e);
    if (onFocus != null) {
      onFocus();
    }
  }, [focusCallbacks, onFocus]);
  useEffect(function () {
    setQuery(initialQuery);
  }, [initialQuery]);
  return {
    isOpened: isOpened && maxIndex > 0,
    isFocused: isFocused,
    shownQuery: shownQuery,
    selectedIndex: currentIndex.selected,
    hoveredIndex: currentIndex.hovered,
    items: data || [],
    onInputFocus: handleInputFocus,
    onInputClick: focusCallbacks.handleClick,
    onInputKeyDown: handleInputKeyDown,
    onInputChange: handleInputChange,
    onListMouseEnter: handleListMouseEnter,
    onListClick: handleSubmitItem,
    onReset: handleReset
  };
}

function useForkRef() {
  var refs = [];
  for (var _i = 0; _i < arguments.length; _i++) {
    refs[_i] = arguments[_i];
  }
  return useCallback(function (v) {
    return refs.forEach(function (ref) {
      if (typeof ref === 'function') {
        ref(v);
      } else if (ref != null) {
        ref.current = v;
      }
    });
  }, [refs]);
}

function isInApp(query) {
  if (typeof window === 'undefined') {
    if (query != null) {
      return query.os_type != null || query.app != null;
    }
    return false;
  }
  // NOTE: It's fine to use sessionStorage here because webview will always
  // try to set these parameters.
  var triggered = false;
  try {
    triggered = sessionStorage.webviewTriggered === 'yes';
  } catch (e) {
    //
  }
  var search = parse(location.search.slice(1));
  var isExpertCenterAndroid = search.pro_app != null && search.os_type === 'Android';
  if (!isExpertCenterAndroid && (triggered || search.os_type != null && search.os_type !== '' || search.app != null)) {
    try {
      sessionStorage.webviewTriggered = 'yes';
    } catch (e) {
      //
    }
    return true;
  }
  return false;
}
function useIsInApp(location) {
  var _a = useState(function () {
      return parse(location.search.slice(1)).app != null;
    }),
    inApp = _a[0],
    setInApp = _a[1];
  var useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(function () {
    setInApp(isInApp());
  }, []);
  return inApp;
}

/**
 * 현재 브라우저가 IE 인지를 체크합니다.
 * @param {boolean} [UseIEDetectorOptions] 체크 옵션
 * @param {boolean} [UseIEDetectorOptions.includeEdge] 엣지 포함 여부
 * @return {boolean} IE 체크 결과
 */
function useIEDetector(_a) {
  var _b = _a === void 0 ? {} : _a,
    _c = _b.includeEdge,
    includeEdge = _c === void 0 ? false : _c;
  var _d = useState(false),
    isIE = _d[0],
    setIE = _d[1];
  useEffect(function () {
    var ua = window.navigator.userAgent;
    var isIE = /MSIE|Trident\//i.test(ua);
    var isEdge = /Edge\//i.test(ua);
    setIE(isIE || includeEdge && isEdge);
  }, [includeEdge]);
  return isIE;
}

function useMatchMedia(query, options) {
  var _a = options || {},
    fallback = _a.fallback,
    populateOnInitialRender = _a.populateOnInitialRender;
  var _b = useState(function () {
      if (!populateOnInitialRender || typeof window === 'undefined') {
        return fallback != null ? fallback : undefined;
      }
      var mediaInstance = window.matchMedia(query);
      return mediaInstance.matches;
    }),
    matches = _b[0],
    setMatches = _b[1];
  useEffect(function () {
    var mediaInstance = window.matchMedia(query);
    var handleChange = function handleChange() {
      return setMatches(mediaInstance.matches);
    };
    mediaInstance.addListener(handleChange);
    setMatches(mediaInstance.matches);
    return function () {
      mediaInstance.removeListener(handleChange);
    };
  }, [query]);
  return {
    matches: matches
  };
}

function useMedia(mediaQuery, initialValue) {
  if (initialValue === void 0) {
    initialValue = false;
  }
  var _a = useState(initialValue),
    isMatched = _a[0],
    setMatched = _a[1];
  useEffect(function () {
    var queryList = window.matchMedia(mediaQuery);
    var handleChange = function handleChange() {
      setMatched(queryList.matches);
    };
    handleChange();
    queryList.addListener(handleChange);
    return function () {
      queryList.removeListener(handleChange);
    };
  }, [mediaQuery]);
  return isMatched;
}

function useResize(ref, handleResize) {
  useEffect(function () {
    var targetNode = ref.current;
    var resizeObserver = null;
    if (window.ResizeObserver != null && targetNode != null) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(targetNode);
    } else {
      window.addEventListener('resize', handleResize);
    }
    return function () {
      if (resizeObserver != null) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, [ref, handleResize]);
}

function useNodeSizeObserver(callback, deps) {
  if (deps === void 0) {
    deps = [];
  }
  var ref = useRef(null);
  var triggerCallback = useCallback(function () {
    if (!ref.current) return;
    callback({
      width: ref.current.clientWidth,
      height: ref.current.clientHeight
    });
  }, [callback]);
  var setRef = useCallback(function (element) {
    ref.current = element;
    triggerCallback();
  }, [triggerCallback]);
  var handleResize = useMemo(function () {
    return debounce(triggerCallback, 500);
  }, [triggerCallback]);
  useResize(ref, handleResize);
  useEffect(function () {
    triggerCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, __spreadArray([triggerCallback], deps, true));
  return setRef;
}

/**
 * OS 타입이 Windows 인지 체크합니다.
 */
function useWindowsOSDetector() {
  var _a = useState(false),
    isWindowsOS = _a[0],
    setWindowsOS = _a[1];
  useEffect(function () {
    var ua = window.navigator.userAgent;
    var isWindowsOS = /'Win32|Win64|Windows|Windows NT|WinCE/gi.test(ua);
    setWindowsOS(isWindowsOS);
  }, []);
  return isWindowsOS;
}

var bizchannelUUID = '@오늘의집_고객센터';
var bizChannelURL = "https://bizmessage.kakao.com/chat/open/".concat(bizchannelUUID);
var channelPublicId = '_TsNUK';
var channelUrl = "https://pf.kakao.com/".concat(channelPublicId, "/chat");
/**
 * iOS 8.34보다 낮은 버전의 app / 그외 를 구분하여 기기에 따른 알맞은 카카오톡 상담하기 채팅창을 엽니다.
 * @param query
 * @returns 카카오톡 상담하기 채팅창을 여는 함수
 */
var useKakaoChat = function useKakaoChat(query) {
  var isApp = getDeviceInfo(query).isApp;
  var openKakaoChat = useCallback(function () {
    if (!isApp || isHigherAppVersion({
      AOS: '0',
      iOS: '8.34',
      query: query
    })) {
      try {
        var popup = openPopup({
          url: bizChannelURL,
          popupOption: 'width=1024, height=800'
        });
        if (!popup) {
          window.location.href = bizChannelURL;
        }
      } catch (_a) {
        window.location.href = bizChannelURL;
      }
    } else {
      window.location.href = channelUrl;
    }
  }, [query, isApp]);
  return {
    openKakaoChat: openKakaoChat
  };
};

var BREAKPOINTS = [['desktop', BREAK_POINT.desktop], ['laptop', BREAK_POINT.laptop], ['tablet', BREAK_POINT.tablet], ['mobile', BREAK_POINT.mobile]];
/**
 * Returns the current breakpoint of the viewport. (mobile, tablet, laptop)
 */
function useResponsive(options) {
  var _a = options !== null && options !== void 0 ? options : {},
    fallbackBreakpoint = _a.fallbackBreakpoint,
    populateOnInitialRender = _a.populateOnInitialRender,
    preventSyncOnInitialRender = _a.preventSyncOnInitialRender;
  var _b = useState(function () {
      if (!populateOnInitialRender) {
        return undefined;
      }
      if (typeof window === 'undefined') {
        return fallbackBreakpoint || undefined;
      }
      var matchingBreakpoint = BREAKPOINTS.find(function (_a) {
        var minWidth = _a[1];
        return window.matchMedia("(min-width: ".concat(minWidth, "px)")).matches;
      });
      return matchingBreakpoint != null ? matchingBreakpoint[0] : fallbackBreakpoint || undefined;
    }),
    breakpoint = _b[0],
    setBreakpoint = _b[1];
  useEffect(function () {
    var mediaRefs = BREAKPOINTS.map(function (_a) {
      var breakpoint = _a[0],
        minWidth = _a[1];
      return [breakpoint, window.matchMedia("(min-width: ".concat(minWidth, "px)"))];
    });
    function update() {
      var hasMatch = false;
      for (var _i = 0, mediaRefs_2 = mediaRefs; _i < mediaRefs_2.length; _i++) {
        var _a = mediaRefs_2[_i],
          breakpoint_1 = _a[0],
          media = _a[1];
        if (!hasMatch && media.matches) {
          setBreakpoint(breakpoint_1);
          hasMatch = true;
        }
      }
    }
    for (var _i = 0, mediaRefs_1 = mediaRefs; _i < mediaRefs_1.length; _i++) {
      var _a = mediaRefs_1[_i],
        media = _a[1];
      media.addListener(update);
    }
    if (!preventSyncOnInitialRender) {
      update();
    }
    return function () {
      for (var _i = 0, mediaRefs_3 = mediaRefs; _i < mediaRefs_3.length; _i++) {
        var _a = mediaRefs_3[_i],
          media = _a[1];
        media.removeListener(update);
      }
    };
  }, [preventSyncOnInitialRender]);
  return {
    breakpoint: breakpoint
  };
}

export { useChanged as a, useForkRef as b, createCachedFetch as c, decomposeHangul as d, useIsInApp as e, useIEDetector as f, useMatchMedia as g, useMedia as h, useNodeSizeObserver as i, useResize as j, useWindowsOSDetector as k, useKakaoChat as l, useResponsive as m, useAutoComplete as u };
//# sourceMappingURL=hooks.js.map
